import {
    ADD_FEATURE_TO_LAND_ASSEMBLY,
    REMOVE_FEATURE_FROM_LAND_ASSEMBLY,
    TOGGLE_LAND_ASSEMBLY_FEATURES,
    SET_LAND_ASSEMBLY_FEATURES,
    FETCH_STATS_REQUEST,
    FETCH_STATS_SUCCESS,
    FETCH_STATS_FAILURE,
    SET_SITE_CARD_CREATOR_OPEN,
    SET_LAND_ASSEMBLY_LAYER
} from './Types';

import axios, { CancelTokenSource } from 'axios';
import _, { debounce } from 'lodash';
import { setHighlightedFeatures } from '../highlights/Actions';
import { Feature } from '../../utility/Types/commonTypes';

const CancelToken = axios.CancelToken;
let refreshStatsSource: CancelTokenSource | null;

const fetchStats = () => ({ type: FETCH_STATS_REQUEST });
const fetchStatsSuccess = (stats: null) => ({ type: FETCH_STATS_SUCCESS, stats });
const fetchStatsError = (error: any) => ({ type: FETCH_STATS_FAILURE, error });

const setSiteCardCreatorOpen = (isOpen: boolean) => ({
    type: SET_SITE_CARD_CREATOR_OPEN,
    isOpen
});

const refreshStats = async (
    dispatch: (arg0: { type: string; stats?: null; error?: any }) => void,
    params: any
) => {
    // refreshStatsSource && refreshStatsSource.cancel('Cancelling already running request')
    // dispatch(fetchStats());
    // console.log('trying stats')
    return await axios
        .get('/api/parcels/intersections', {
            params: params,
            cancelToken: (refreshStatsSource = axios.CancelToken.source()).token
        })
        .then(response => {
            refreshStatsSource = null;
            dispatch(fetchStatsSuccess(response.data));
        })
        .catch(error => {
            refreshStatsSource = null;
            if (axios.isCancel(error)) {
                console.log('cancel', error);
            } else {
                dispatch(fetchStatsError(error.response));
                console.error(error);
            }
        });
};

const debouncedRefreshStats = debounce(refreshStats, 2000);

export const setLandAssemblyLayer = (layerId: number) => {
    refreshStatsSource?.cancel('Cancelling already running request');
    return { type: SET_LAND_ASSEMBLY_LAYER, layerId };
};

export function addFeatureToLandAssemblyAsync(feature: any) {
    return (dispatch: (dispatch: any) => Promise<void>, getState: () => any) => {
        dispatch({ type: ADD_FEATURE_TO_LAND_ASSEMBLY, feature });
        const layerId = getState().landAssembly.layerId;
        const layer = getState().layers.layers.find(
            (l: { id: number }) => l.id === layerId
        );
        const updatedFeatures = getState().landAssembly.features;
        const updatedFeatureIds = updatedFeatures.map(
            (feature: Feature) => feature.featureId
        );

        const highlightedFeatures = [
            {
                key: `${layer.system_name}_selected`,
                layerSystemName: layer.system_name,
                features: _.cloneDeep(updatedFeatures)
            }
        ];
        dispatch(setHighlightedFeatures(highlightedFeatures));

        dispatch(refreshStatsAsync(layerId, updatedFeatureIds));
        // debouncedRefreshStats(dispatch, {polygon_ids: featureIds});
    };
}

export function removeFeatureFromLandAssemblyAsync(feature: Feature) {
    return (dispatch: (dispatch: any) => Promise<void>, getState: () => any) => {
        dispatch({ type: REMOVE_FEATURE_FROM_LAND_ASSEMBLY, feature });
        const layerId = getState().landAssembly.layerId;
        const layer = getState().layers.layers.find(
            (l: { id: number }) => l.id === layerId
        );
        const updatedFeatures = getState().landAssembly.features;
        const updatedFeatureIds = updatedFeatures.map(
            (feature: Feature) => feature.featureId
        );

        const highlightedFeatures = [
            {
                key: `${layer.system_name}_selected`,
                layerSystemName: layer.system_name,
                features: _.cloneDeep(updatedFeatures)
            }
        ];
        dispatch(setHighlightedFeatures(highlightedFeatures));

        dispatch(refreshStatsAsync(layerId, updatedFeatureIds));
        // debouncedRefreshStats(dispatch, {polygon_ids: featureIds});
    };
}

export function toggleLandAssemblyFeaturesAsync(features: Feature[]) {
    return (dispatch: (dispatch: any) => Promise<void>, getState: () => any) => {
        dispatch({ type: TOGGLE_LAND_ASSEMBLY_FEATURES, features });
        const layerId = getState().landAssembly.layerId;
        const layer = getState().layers.layers.find(
            (l: { id: number }) => l.id === layerId
        );
        const updatedFeatures = getState().landAssembly.features;
        const updatedFeatureIds = updatedFeatures.map(
            (feature: Feature) => feature.featureId
        );

        const highlightedFeatures = [
            {
                key: `${layer.system_name}_selected`,
                layerSystemName: layer.system_name,
                features: _.cloneDeep(updatedFeatures)
            }
        ];
        dispatch(setHighlightedFeatures(highlightedFeatures));

        dispatch(refreshStatsAsync(layerId, updatedFeatureIds));
        // debouncedRefreshStats(dispatch, {polygon_ids: featureIds});
    };
}

export function setLandAssemblyFeaturesAsync(layerId: number, features: Feature[]) {
    return (dispatch: (dispatch: any) => Promise<void>, getState: () => any) => {
        dispatch({ type: SET_LAND_ASSEMBLY_FEATURES, layerId, features });
        const layer = getState().layers.layers.find(
            (l: { id: number }) => l.id === layerId
        );
        const updatedFeatures = getState().landAssembly.features;
        const updatedFeatureIds = updatedFeatures.map(
            (feature: Feature) => feature.featureId
        );

        const highlightedFeatures = [
            {
                key: `${layer.system_name}_selected`,
                layerSystemName: layer.system_name,
                features: _.cloneDeep(updatedFeatures)
            }
        ];
        dispatch(setHighlightedFeatures(highlightedFeatures));

        dispatch(refreshStatsAsync(layerId, updatedFeatureIds));
        // debouncedRefreshStats(dispatch, {polygon_ids: featureIds});
    };
}

export function refreshStatsAsync(layerId: number, featureIds: number[]) {
    return (
        dispatch:
            | ((arg0: {
                  type: string;
                  stats?: null | undefined;
                  error?: any;
              }) => void)
            | ((arg0: { type: string; stats?: null }) => void),
        getState: () => any
    ) => {
        refreshStatsSource?.cancel('Cancelling already running request');
        dispatch(fetchStats());

        if (featureIds.length === 0) {
            debouncedRefreshStats.cancel();
            dispatch(fetchStatsSuccess(null));
        } else {
            const weightingProfileId = 
                getState().user.prefs?.defaultAnalysisProfiles?.find(ap => ap.layerId === layerId).profileId ||
                getState().user.features.analysis?.layers?.find(l => l.layerId === layerId)?.profiles[0];
            debouncedRefreshStats(dispatch, {
                layer_id: layerId,
                polygon_ids: featureIds,
                weighting_profile_id: weightingProfileId
            });
        }
    };
}

export function openSiteCardCreator() {
    return (dispatch: (arg0: { type: string; isOpen: boolean }) => void) => {
        dispatch(setSiteCardCreatorOpen(true));
    };
}

export function closeSiteCardCreator() {
    return (dispatch: (arg0: { type: string; isOpen: boolean }) => void) => {
        dispatch(setSiteCardCreatorOpen(false));
    };
}
